import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { AgnoChatMessage } from "@/hooks/use-agno-chat";
import type { LoadMoreMessagesOptions } from "@/hooks/use-session-message-feed";
import {
  parseConversationTarget,
  type ConversationTarget,
  type ConversationTargetParseResult,
} from "@/lib/conversation-target";
import type { ThreadAnchorRef } from "@/lib/session";
import type { NavigationTargetLoadResult } from "@/hooks/use-session-message-feed";

const INITIAL_PAGE_BUDGET = 10;

export type ConversationDeepLinkStatus =
  | "idle"
  | "invalid"
  | "locating"
  | "paused"
  | "retryable-error"
  | "ready"
  | "missing"
  | "cancelled";

export interface ConversationDeepLinkState {
  requestId: number;
  target: ConversationTarget | null;
  targetMessageId: string | null;
  status: ConversationDeepLinkStatus;
  searchedPages: number;
  continueSearch: () => void;
  retry: () => void;
  cancel: () => void;
}

function currentTarget(sessionId: string) {
  if (typeof window === "undefined") return { state: "none" } as const;
  const match = window.location.pathname.match(/^\/session\/([^/]+)\/?$/);
  if (!match || decodeURIComponent(match[1]) !== sessionId) return { state: "none" } as const;
  return parseConversationTarget(window.location.hash);
}

function directMessageId(target: ConversationTarget, anchors: readonly ThreadAnchorRef[]): string | null {
  if (target.kind === "message") return target.messageId;
  if (target.kind === "turn") return target.turnId;
  const verified = anchors.find((anchor) => anchor.child_session_id === target.threadId);
  if (!verified) return null;
  if (target.messageId && target.messageId !== verified.source_message_id) return null;
  return verified.source_message_id;
}

/**
 * Resolve v1 URL targets against loaded history, with a bounded old-server
 * fallback that reuses the existing serialized backwards paginator.
 */
export function useConversationDeepLink({
  sessionId,
  messages,
  threadAnchors,
  hasMore,
  loadOlder,
  hasLoaded,
  loadNavigationTarget,
}: {
  sessionId: string;
  messages: readonly AgnoChatMessage[];
  threadAnchors: readonly ThreadAnchorRef[];
  hasMore: boolean;
  loadOlder: (options?: LoadMoreMessagesOptions) => Promise<void>;
  hasLoaded: boolean;
  loadNavigationTarget: (
    target: ConversationTarget,
    signal: AbortSignal,
  ) => Promise<NavigationTargetLoadResult>;
}): ConversationDeepLinkState {
  const requestCounterRef = useRef(0);
  const loadingRef = useRef(false);
  const windowControllerRef = useRef<AbortController | null>(null);
  const [request, setRequest] = useState<{
    id: number;
    parsed: ConversationTargetParseResult;
    cancelled: boolean;
    budget: number;
  }>(() => ({
    id: 0,
    parsed: { state: "none" } as const,
    cancelled: false,
    budget: INITIAL_PAGE_BUDGET,
  }));
  const [searchedPages, setSearchedPages] = useState(0);
  const [windowResolution, setWindowResolution] = useState<{
    requestId: number;
    state: "idle" | "loading" | "unsupported" | "error" | "missing" | "denied";
    messageId: string | null;
  }>({ requestId: 0, state: "idle", messageId: null });

  useEffect(() => {
    const read = () => {
      loadingRef.current = false;
      windowControllerRef.current?.abort();
      setSearchedPages(0);
      const id = ++requestCounterRef.current;
      setWindowResolution({ requestId: id, state: "idle", messageId: null });
      setRequest({
        id,
        parsed: currentTarget(sessionId),
        cancelled: false,
        budget: INITIAL_PAGE_BUDGET,
      });
    };
    read();
    window.addEventListener("hashchange", read);
    window.addEventListener("popstate", read);
    return () => {
      windowControllerRef.current?.abort();
      window.removeEventListener("hashchange", read);
      window.removeEventListener("popstate", read);
    };
  }, [sessionId]);

  const target = request.parsed.state === "valid" ? request.parsed.target : null;
  const targetMessageId = useMemo(
    () => windowResolution.requestId === request.id && windowResolution.messageId
      ? windowResolution.messageId
      : target ? directMessageId(target, threadAnchors) : null,
    [request.id, target, threadAnchors, windowResolution],
  );
  const isLoaded = Boolean(
    targetMessageId && messages.some((message) => message.id === targetMessageId),
  );
  const unresolvedThread = target?.kind === "thread" && targetMessageId === null;

  let status: ConversationDeepLinkStatus = "idle";
  if (request.cancelled) status = "cancelled";
  else if (request.parsed.state === "invalid") status = "invalid";
  else if (target) {
    if (isLoaded) status = "ready";
    else if (windowResolution.state === "error") status = "retryable-error";
    else if (windowResolution.state === "missing" || windowResolution.state === "denied") status = "missing";
    else if (!hasLoaded || windowResolution.state === "idle" || windowResolution.state === "loading") status = "locating";
    else if (hasMore && searchedPages < request.budget) status = "locating";
    else if (hasMore) status = "paused";
    else status = unresolvedThread ? "missing" : "missing";
  }

  useEffect(() => {
    if (
      !target
      || isLoaded
      || !hasLoaded
      || windowResolution.requestId !== request.id
      || windowResolution.state !== "idle"
    ) return;
    const controller = new AbortController();
    windowControllerRef.current?.abort();
    windowControllerRef.current = controller;
    const requestId = request.id;
    setWindowResolution({ requestId, state: "loading", messageId: null });
    void loadNavigationTarget(target, controller.signal)
      .then((result) => {
        if (controller.signal.aborted || requestCounterRef.current !== requestId) return;
        if (result.state === "loaded") {
          setWindowResolution({ requestId, state: "loading", messageId: result.messageId });
        } else {
          setWindowResolution({ requestId, state: result.state, messageId: null });
        }
      })
      .catch((error) => {
        if (controller.signal.aborted || (error instanceof DOMException && error.name === "AbortError")) return;
        if (requestCounterRef.current === requestId) {
          setWindowResolution({ requestId, state: "error", messageId: null });
        }
      });
  }, [hasLoaded, isLoaded, loadNavigationTarget, request.id, target, windowResolution]);

  useEffect(() => {
    if (
      status !== "locating"
      || windowResolution.state !== "unsupported"
      || loadingRef.current
    ) return;
    const requestId = request.id;
    loadingRef.current = true;
    void loadOlder().finally(() => {
      if (requestCounterRef.current === requestId) {
        loadingRef.current = false;
        setSearchedPages((count) => count + 1);
      }
    });
  }, [loadOlder, request.id, searchedPages, status, windowResolution.state]);

  const continueSearch = useCallback(() => {
    loadingRef.current = false;
    setRequest((current) => ({ ...current, budget: current.budget + INITIAL_PAGE_BUDGET }));
  }, []);
  const cancel = useCallback(() => {
    loadingRef.current = false;
    windowControllerRef.current?.abort();
    setRequest((current) => ({ ...current, cancelled: true }));
  }, []);
  const retry = useCallback(() => {
    loadingRef.current = false;
    windowControllerRef.current?.abort();
    setWindowResolution({ requestId: request.id, state: "idle", messageId: null });
  }, [request.id]);

  return {
    requestId: request.id,
    target,
    targetMessageId,
    status,
    searchedPages,
    continueSearch,
    retry,
    cancel,
  };
}
